import React, { useContext, createContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom'
import { UserContext } from './userContext'; // Import your UserContext
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { extractApiError } from '../utils/extract-api-error';
import { getLocalStorageItem, setLocalStorageItem } from '../utils/browser-storage';

export const ApiContext = createContext();

const toStatusText = (status) => {
    if (!status) return 'UNKNOWN';
    if (status >= 500) return 'SERVER_ERROR';
    if (status >= 400) return 'CLIENT_ERROR';
    return 'HTTP_ERROR';
};

const getPathFromUrl = (url) => {
    try {
        return new URL(String(url), window.location.origin).pathname || String(url);
    } catch (_error) {
        return String(url || '');
    }
};

const buildApiErrorMessage = ({ method, url, status, code }) => {
    const path = getPathFromUrl(url) || 'unknown_endpoint';
    const statusToken = status ? String(status) : toStatusText(status);
    return `API ${method} ${path} failed (${statusToken}${code ? `:${code}` : ''})`;
};

const SKIP_GLOBAL_ERROR_TOAST_HEADER = 'X-Leasehub-Skip-Global-Error-Toast';

const getInitialYear = () => {
    const currentYear = new Date().getFullYear();
    const storedYear = getLocalStorageItem(`year-${currentYear}`);
    if (storedYear) return storedYear;

    setLocalStorageItem(`year-${currentYear}`, currentYear);
    return currentYear;
};

export const ApiProvider = ({ children }) => {
    const { accessTokenRef, refreshTokens } = useContext(UserContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [year, setYearState] = useState(getInitialYear);

    const setYear = (year) => {
        const todaysYear = new Date().getFullYear();
        setLocalStorageItem(`year-${todaysYear}`, year);
        setYearState(year);
    }


    const makeRequest = async ({ url, method = 'GET', data = null, params = {}, headers = {} }) => {
        // No access token means the user is logged out. Don't fire authenticated
        // requests — they'd only 401 and trigger a useless token refresh (which
        // also 401s once the refresh cookie is cleared). This prevents in-flight
        // polling effects (sidebar unread count, showings metrics) from hitting
        // the API during/after logout. Callers already handle thrown errors.
        if (!accessTokenRef.current) {
            const authError = new Error('Not authenticated');
            authError.status = 401;
            authError.response = { status: 401, data: null };
            authError.isAuthSkipped = true;
            throw authError;
        }
        // console.log(url)
        let fullUrl;
        if(Object.keys(params).length !== 0){
            fullUrl = `${url}?${new URLSearchParams(params).toString()}`;
        }else{
            fullUrl = url
        }
        
        const addParamToUrl = (url, paramName, paramValue) => {
          const separator = url.includes("?") ? "&" : "?";
          return `${url}${separator}${paramName}=${encodeURIComponent(
            paramValue
          )}`;
        };

        if (method === 'GET' && !/insights|messaging|properties/.test(location.pathname))fullUrl = addParamToUrl(fullUrl, "year", year);

        const requestHeaders = {
            ...headers,
            // Read the ref so we always send the freshest token — `setTokens` mutates a
            // ref without re-rendering, so a destructured `accessToken` would go stale.
            'Authorization': `Bearer ${accessTokenRef.current}`,
            'Content-Type': 'application/json',
        };

        const skipGlobalErrorToast =
            String(requestHeaders[SKIP_GLOBAL_ERROR_TOAST_HEADER] || '').toLowerCase() === '1';
        delete requestHeaders[SKIP_GLOBAL_ERROR_TOAST_HEADER];

        const options = {
            method,
            headers: requestHeaders,
            body: null,
        };

        if (data) {
            if (data instanceof FormData) {
                options.body = data;
                delete options.headers['Content-Type']; // FormData should not have Content-Type header
            } else {
                options.body = JSON.stringify(data);
            }
        }

        try {
            let response = await fetch(fullUrl.toString(), options);
            if (response.status === 401) {
                const newToken = await refreshTokens(); // null when refresh failed (already logging out)
                if (newToken) {
                    options.headers['Authorization'] = `Bearer ${newToken}`; // use the refreshed token, not the stale one
                    response = await fetch(fullUrl.toString(), options); // Retry the request
                }
            }
            if (response.status === 403) {
                navigate('/403', { replace: true });
                const forbidden = new Error("You don't have permission to do that.");
                forbidden.status = 403;
                forbidden.response = { status: 403, data: null };
                throw forbidden;
            }
            if (!response.ok) {
                let errorPayload = null;
                try {
                    errorPayload = await response.json();
                } catch (_jsonError) {
                    errorPayload = null;
                }

                // Build an axios-shaped error so extractApiError handles it uniformly.
                const status = response.status;
                const endpointPath = getPathFromUrl(fullUrl);
                const errorCode = errorPayload?.code || null;
                const errorMessage = buildApiErrorMessage({
                    method,
                    url: fullUrl,
                    status,
                    code: errorCode,
                });
                const error = new Error(errorMessage);
                error.status = status;
                error.data = errorPayload;
                error.response = { status, data: errorPayload };
                error.url = fullUrl;
                error.method = method;
                error.sentryCaptured = false;

                // Don't report expected product errors to Sentry — only real bugs.
                const skipSentry = new Set([401, 403, 404, 409, 422]);
                if (status >= 500 || !skipSentry.has(status)) {
                    try {
                        Sentry.withScope((scope) => {
                            scope.setTag('http.status', String(status));
                            scope.setTag('http.method', method);
                            scope.setTag('api.endpoint', endpointPath || 'unknown_endpoint');
                            if (errorCode) scope.setTag('api.code', String(errorCode));
                            if (errorPayload?.request_id) {
                                scope.setTag('request_id', errorPayload.request_id);
                            }
                            scope.setFingerprint([
                                'client-api',
                                method,
                                endpointPath || 'unknown_endpoint',
                                String(status || 'unknown_status'),
                                String(errorCode || 'no_code'),
                            ]);
                            scope.setContext('api_request', {
                                url: fullUrl,
                                endpoint: endpointPath,
                                method,
                                status,
                                code: errorCode,
                                requestId: errorPayload?.request_id,
                            });
                            Sentry.captureException(error);
                        });
                        error.sentryCaptured = true;
                    } catch {
                        // ignore Sentry failures
                    }
                }

                throw error;
            }

            // 204 / empty bodies (e.g. DELETE) have nothing to parse.
            const body = await response.text();
            return body ? JSON.parse(body) : null;
        } catch (error) {
            // Show backend's actual message via the normalizer.
            const normalized = extractApiError(error);
            const endpointPath = getPathFromUrl(fullUrl);

            if (
                !error?.sentryCaptured &&
                (normalized.isNetworkError || (normalized.status && normalized.status >= 500))
            ) {
                try {
                    const statusToken = normalized.isNetworkError
                        ? 'network'
                        : String(normalized.status || 'unknown_status');
                    const sentryError = new Error(
                        normalized.isNetworkError
                            ? `API ${method} ${endpointPath || 'unknown_endpoint'} failed (NETWORK)`
                            : (error?.message || buildApiErrorMessage({
                                method,
                                url: fullUrl,
                                status: normalized.status,
                                code: normalized.code,
                            }))
                    );

                    Sentry.withScope((scope) => {
                        scope.setTag('http.status', statusToken);
                        scope.setTag('http.method', method);
                        scope.setTag('api.endpoint', endpointPath || 'unknown_endpoint');
                        if (normalized.code) scope.setTag('api.code', String(normalized.code));
                        if (normalized.requestId) scope.setTag('request_id', String(normalized.requestId));
                        scope.setFingerprint([
                            'client-api',
                            method,
                            endpointPath || 'unknown_endpoint',
                            statusToken,
                            String(normalized.code || 'no_code'),
                        ]);
                        scope.setContext('api_request', {
                            url: fullUrl,
                            endpoint: endpointPath,
                            method,
                            status: normalized.status || null,
                            code: normalized.code || null,
                            requestId: normalized.requestId || null,
                            isNetworkError: normalized.isNetworkError,
                        });
                        Sentry.captureException(sentryError);
                    });
                    if (error && typeof error === 'object') {
                        error.sentryCaptured = true;
                    }
                } catch {
                    // ignore Sentry failures
                }
            }

            const isBackgroundLoadError =
                method === 'GET' &&
                (normalized.isNetworkError || (normalized.status && normalized.status >= 500));
            const toastId = [
                isBackgroundLoadError ? 'api-load-error' : 'api-error',
                method,
                normalized.status || 'network',
                isBackgroundLoadError ? 'background' : String(fullUrl).split('?')[0],
            ].join(':');

            // 413 is already shown by the fetch hook's RequestTooLargeDialog.
            // Still mark it shown so callers don't stack their own toast.
            const isRequestTooLarge = normalized.status === 413;

            if (isRequestTooLarge || !skipGlobalErrorToast) {
                if (!isRequestTooLarge) {
                    toast.error(normalized.message, { toastId });
                }
                if (error && typeof error === 'object') {
                    error.toastShown = true;
                }
            }
            throw error;
        }
    };

    const apiClient = {
        get: (url, params = {}, headers = {}) => makeRequest({ url, method: 'GET', params, headers }),
        post: (url, data, params = {}, headers = {}) => makeRequest({ url, method: 'POST', data, params, headers }),
        put: (url, data, params = {}, headers = {}) => makeRequest({ url, method: 'PUT', data, params, headers }),
        patch: (url, data, params = {}, headers = {}) => makeRequest({ url, method: 'PATCH', data, params, headers }),
        delete: (url, params = {}, headers = {}) => makeRequest({ url, method: 'DELETE', params, headers }),
        year,
        setYear,
    };

    return (
        <ApiContext.Provider value={apiClient}>
            {children}
        </ApiContext.Provider>
    );
};
